/**
 * Client-side volunteers API module — replaces volunteers.actions.ts Server Actions.
 *
 * Key changes:
 * - No "use server" directive
 * - No revalidateTag — callers use queryClient.invalidateQueries()
 * - No Zod validation — backend handles it via class-validator
 * - No Date→ISO/parseInt data transformation — backend handles it via @Transform()
 * - Batch fetch uses POST /volunteer/batch instead of N+1 individual calls
 */

import { ADMIN, USER, PROFILE } from "@/shared/constants/backend-api"
import { authFetchJson, authFetch } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import {
  PaginatedVolunteersResponse,
  VolunteerTableRowsResponse,
} from "../types/volunteers-responses.type"
import { VolunteerFormType, UpdateVolunteerFormType } from "../schemas/volunteers.schema"
import { Volunteer, NonHomeownerUser } from "../types/volunteers.type"
import { UpdateUserFormType } from "../schemas/update-user.schema"
import { User } from "../../homeowners/types/backend/user.type"
import { VolunteerQueryParamsType } from "../schemas/volunteer-query.schema"
import { buildUrl } from "@/shared/lib/utils"

// ---------------------------------------------------------------------------
// Query key constants (for React Query cache invalidation by callers)
// ---------------------------------------------------------------------------

export const VOLUNTEER_QUERY_KEYS = {
  all: ["volunteers"] as const,
  list: () => [...VOLUNTEER_QUERY_KEYS.all, "list"] as const,
  tableRows: () => [...VOLUNTEER_QUERY_KEYS.all, "table-rows"] as const,
  detail: (id: number) => [...VOLUNTEER_QUERY_KEYS.all, "detail", id] as const,
  locations: () => [...VOLUNTEER_QUERY_KEYS.all, "locations"] as const,
  organizations: () => [...VOLUNTEER_QUERY_KEYS.all, "organizations"] as const,
  user: (userId: number) => ["user", userId] as const,
}

// ---------------------------------------------------------------------------
// Batch fetch (replaces N+1 getVolunteersByIds)
// ---------------------------------------------------------------------------

export async function getVolunteersByIds(
  volunteerIds: number[],
): Promise<ApiResponse<Volunteer[]>> {
  if (!volunteerIds || volunteerIds.length === 0) {
    return { success: true, data: [] }
  }

  return authFetchJson<Volunteer[]>("/volunteer/batch", {
    method: "POST",
    body: JSON.stringify({ ids: volunteerIds }),
  })
}

// ---------------------------------------------------------------------------
// List / paginated queries
// ---------------------------------------------------------------------------

export async function getVolunteers(
  queryParams: VolunteerQueryParamsType,
): Promise<ApiResponse<PaginatedVolunteersResponse>> {
  const url = buildUrl(ADMIN.base, queryParams as any)
  return authFetchJson<PaginatedVolunteersResponse>(url)
}

export async function getVolunteerTableRows(
  queryParams: VolunteerQueryParamsType,
  groupId?: string | null,
): Promise<ApiResponse<VolunteerTableRowsResponse>> {
  const params: Record<string, unknown> = { ...queryParams }
  if (groupId) params.groupId = groupId
  const url = buildUrl(ADMIN.tableRows, params as any)
  return authFetchJson<VolunteerTableRowsResponse>(url)
}

// ---------------------------------------------------------------------------
// Single volunteer
// ---------------------------------------------------------------------------

export async function getVolunteerById(
  volunteerId: number,
): Promise<ApiResponse<Volunteer>> {
  return authFetchJson<Volunteer>(ADMIN.byId(volunteerId))
}

export async function getNonHomeownerUserById(
  userId: number,
): Promise<ApiResponse<NonHomeownerUser>> {
  return authFetchJson<NonHomeownerUser>(ADMIN.nonHomeownerUserById(userId))
}

// ---------------------------------------------------------------------------
// Locations & organizations
// ---------------------------------------------------------------------------

export async function getVolunteerLocations(): Promise<ApiResponse<string[]>> {
  return authFetchJson<string[]>(ADMIN.locations)
}

export async function getVolunteerOrganizationNames(): Promise<ApiResponse<string[]>> {
  return authFetchJson<string[]>(ADMIN.organizations)
}

// ---------------------------------------------------------------------------
// Create volunteer
// ---------------------------------------------------------------------------

export async function createVolunteer(
  volunteer: VolunteerFormType,
): Promise<ApiResponse<Volunteer>> {
  // Build FormData for file upload support
  const formData = new FormData()

  for (const [key, value] of Object.entries(volunteer)) {
    if (value === undefined || value === null) continue

    if (key === "servePeriods" && Array.isArray(value)) {
      // Stringify serve periods for FormData
      const periods = value
        .filter((p: any) => p?.from && p?.to)
        .map((p: any) => ({
          from: p.from instanceof Date ? p.from.toISOString() : p.from,
          to: p.to instanceof Date ? p.to.toISOString() : p.to,
          ...(p.regionId != null ? { regionId: Number(p.regionId) } : {}),
        }))
      if (periods.length > 0) formData.append(key, JSON.stringify(periods))
      continue
    }

    if (key === "dateOfBirth") {
      const d = value instanceof Date ? value.toISOString().slice(0, 10) : String(value)
      if (d) formData.append(key, d)
      continue
    }

    if (key === "profilePicture" && value instanceof File) {
      formData.append(key, value)
      continue
    }

    if (key === "profilePicture") continue // skip non-file

    formData.append(key, String(value))
  }

  return authFetchJson<Volunteer>(ADMIN.base, {
    method: "POST",
    body: formData,
  })
}

// ---------------------------------------------------------------------------
// Update volunteer
// ---------------------------------------------------------------------------

export async function updateVolunteer(
  volunteerId: number,
  volunteer: UpdateVolunteerFormType,
): Promise<ApiResponse<Volunteer>> {
  const formData = new FormData()

  for (const [key, value] of Object.entries(volunteer)) {
    if (value === undefined || value === null) continue

    if (key === "servePeriods" && Array.isArray(value)) {
      const periods = value
        .filter((p: any) => p?.from && p?.to)
        .map((p: any) => ({
          from: p.from instanceof Date ? p.from.toISOString() : p.from,
          to: p.to instanceof Date ? p.to.toISOString() : p.to,
        }))
      formData.append(key, JSON.stringify(periods))
      continue
    }

    if (key === "profilePicture" && value instanceof File) {
      formData.append(key, value)
      continue
    }

    if (key === "profilePicture") continue

    formData.append(key, String(value))
  }

  return authFetchJson<Volunteer>(ADMIN.byId(volunteerId), {
    method: "PATCH",
    body: formData,
  })
}

// ---------------------------------------------------------------------------
// Delete volunteer
// ---------------------------------------------------------------------------

export async function deleteVolunteer(
  volunteerId: number,
): Promise<ApiResponse<Volunteer>> {
  return authFetchJson<Volunteer>(ADMIN.byId(volunteerId), {
    method: "DELETE",
  })
}

// ---------------------------------------------------------------------------
// Update user (non-volunteer user entity)
// ---------------------------------------------------------------------------

export async function updateUser(
  userId: number,
  userData: UpdateUserFormType,
): Promise<ApiResponse<User>> {
  const hasProfilePicture =
    typeof userData.profilePicture === "object" && userData.profilePicture instanceof File

  if (hasProfilePicture) {
    // Send as FormData for file upload
    const formData = new FormData()
    for (const [key, value] of Object.entries(userData)) {
      if (value === undefined || value === null) continue
      if (key === "servePeriods" && Array.isArray(value)) {
        const periods = value
          .filter((p: any) => p?.from && p?.to)
          .map((p: any) => ({
            from: p.from instanceof Date ? p.from.toISOString() : p.from,
            to: p.to instanceof Date ? p.to.toISOString() : p.to,
            ...(p.regionId != null ? { regionId: Number(p.regionId) } : {}),
          }))
        formData.append(key, JSON.stringify(periods))
        continue
      }
      if (key === "availabilityRange" && typeof value === "object") {
        formData.append(key, JSON.stringify(value))
        continue
      }
      if (key === "selectedRegions" && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value))
        continue
      }
      if (key === "profilePicture" && value instanceof File) {
        formData.append(key, value)
        continue
      }
      if (key === "profilePicture") continue
      formData.append(key, String(value))
    }
    return authFetchJson<User>(USER.update(userId), {
      method: "PATCH",
      body: formData,
    })
  }

  // No file — send as JSON
  return authFetchJson<User>(USER.update(userId), {
    method: "PATCH",
    body: JSON.stringify(userData),
  })
}

// ---------------------------------------------------------------------------
// Delete user
// ---------------------------------------------------------------------------

export async function deleteUser(
  userId: number,
): Promise<ApiResponse<string>> {
  return authFetchJson<string>(USER.delete(userId), {
    method: "DELETE",
  })
}

// ---------------------------------------------------------------------------
// Group volunteers
// ---------------------------------------------------------------------------

export async function addGroupVolunteer(
  groupId: string,
  userId: number,
): Promise<ApiResponse<{ success: boolean }>> {
  return authFetchJson<{ success: boolean }>(ADMIN.groupVolunteersBase, {
    method: "POST",
    body: JSON.stringify({ groupId, userId }),
  })
}

export async function removeGroupVolunteer(
  groupId: string,
  userId: number,
): Promise<ApiResponse<{ success: boolean }>> {
  const url = `${ADMIN.groupVolunteersBase}?groupId=${encodeURIComponent(groupId)}&userId=${userId}`
  return authFetchJson<{ success: boolean }>(url, { method: "DELETE" })
}

// ---------------------------------------------------------------------------
// AR Leadership Dates
// ---------------------------------------------------------------------------

export async function getArLeadershipDates(
  regionId?: number | null,
  activeTodayBounds?: {
    activeTodayStart: string
    activeTodayEnd: string
    clientTimeZone?: string
  },
): Promise<ApiResponse<VolunteerTableRowsResponse>> {
  const params: VolunteerQueryParamsType = {
    page: 1,
    limit: 500,
    roles: ["Admin", "BaseLeader"],
    ...activeTodayBounds,
  }
  if (regionId != null && regionId > 0) {
    params.serveDatesRegionId = regionId
  }
  return getVolunteerTableRows(params)
}
